import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import bem from 'utils/bem';
import Text from 'components/Text';
import { AudioItem } from 'components/Audio/Item';
import { DownloadItem } from 'components/DownloadItem';
import { OtvetAudioPlayer } from 'components/Audio';
import { useAudioPlayer } from 'components/Audio/useAudioPlayer';
import Model from '../../../model';
import Page from '../Page';
import { toast } from 'react-hot-toast';
import { NoPaymentPlaceholder } from './NoPaymentPlaceholder';
import { NoFilesPlaceholder } from './NoFilesPlaceholder';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { VideoItem } from 'components/VideoItem';
import './index.sass';

const { block, el } = bem('course-materials');

const isAudio = (file) => {
    return file.file.endsWith('.mp3');
}
const isVideo = (file) => {
    return Boolean(file.urlIframe);
}

const CourseGenericMaterials = () => {
    const { course } = useParams();
    const { user } = Model;
    const [ loading, setLoading ] = useState(false);
    const [ item, setItem ] = useState(null);
    const player = useRef(null);
    const [ tracksAmount, setTracksAmount ] = useState( 0);

    const { activeTrack, showPlayer, onTrackClick, isPlaying, ...playerProps }
        = useAudioPlayer({ player, length: tracksAmount });

    useEffect(() => {
        setLoading(true);
        user.getDigitalByUrl(course)
            .then((digital) => {
                setLoading(false);
                if (digital !== undefined) {
                    setItem(digital);
                }
            })
            .catch((error) => {
                setLoading(false)
                if (error.message) {
                    toast.error(error.message);
                } else {
                    toast.error(JSON.stringify(error));
                }
            });
    }, []);

    useEffect(() => {
        if (item) {
            const audioFiles = item.digital?.files.filter(file => !!file.isActive && isAudio(file));
            setTracksAmount(audioFiles.length);
        }
    }, [item]);

    const files = useMemo(() => item?.digital?.files.filter(file => !!file.isActive), [item]);
    const audioFiles = useMemo(() =>  item?.digital?.files.filter(file => !!file.isActive && isAudio(file)), [item]);

    if (loading) {
        return <LoadingPlaceholder className={block()} loading={loading}/>
    }

    if (!item) {
        return <NoPaymentPlaceholder className={block()} username={user.username} />
    }

    if (files.length === 0) {
        return <NoFilesPlaceholder className={block()}/>
    }

    const hasManySections = () => files.filter(file => !!file.urlIframe).length > 1;

    const filesToRender = files.map((file, index) => {
        if (isVideo(file)) {
            return <VideoItem key={`${file.title}-${index}`} title={hasManySections ? file.title : null} src={file.urlIframe} />;
        } else if (isAudio(file)) {
            return <AudioItem key={`${file.title}-${index}`} title={ file.title } onClick={ onTrackClick(index) } isPlaying={ activeTrack === index && isPlaying } />;
        } else {
            return <DownloadItem key={`${file.title}-${index}`} link={ `/media/${ file.file }` } title={ file.title } />;
        }
    })

    return (
        <Page className={ block() } title={ item.name }>
            <div className={ el('content') }>
                <div className={ el('top-block') }>
                    <div className={ el('description') }>
                        <Text tag='div'>
                            { item.digital.descriptionShort }
                        </Text>
                    </div>
                    <div className={ el('course', { 'many': hasManySections }) }>
                        { filesToRender }
                    </div>
                </div>
            </div>
            <div className={ el('player', { 'hidden': !showPlayer }) }>
                <OtvetAudioPlayer
                    src={ `/media/${ audioFiles?.[activeTrack]?.file }` }
                    ref={ player }
                    { ...playerProps }
                />
            </div>
        </Page>
    )
}

export default observer(CourseGenericMaterials);
